import { db, PendingDelivery } from "./db";
import { deliveriesApi, type CreateDeliveryRequest } from "@api/modules/deliveries.api";

let isSyncing = false;
let syncListeners: Array<() => void> = [];

export const addSyncListener = (listener: () => void) => {
    syncListeners.push(listener);
    return () => {
        syncListeners = syncListeners.filter((l) => l !== listener);
    };
};

const notifyListeners = () => {
    syncListeners.forEach((listener) => listener());
};

export const syncPendingDeliveries = async (): Promise<void> => {
    if (isSyncing) {
        return;
    }

    isSyncing = true;
    notifyListeners();

    try {
        const pending: PendingDelivery[] = await db.pendingDeliveries
            .where("status")
            .anyOf(["PENDING", "FAILED"])
            .toArray();

        if (pending.length === 0) {
            return;
        }

        const batch: CreateDeliveryRequest[] = pending.map((p) => ({
            shiftId: p.shiftId,
            customerId: p.customerId,
            productId: p.productId,
            fullGiven: p.fullGiven,
            emptyReceived: p.emptyReceived,
            cashCollected: p.cashCollected,
            clientTxnId: p.clientTxnId,
        }));

        try {
            const results = await deliveriesApi.syncBatch(batch);

            for (const item of pending) {
                const result = results.find((r) => r.clientTxnId === item.clientTxnId);
                if (result && result.status !== "error") {
                    await db.pendingDeliveries.update(item.id!, {
                        status: "SYNCED",
                        syncedAt: new Date().toISOString(),
                    });
                } else {
                    await db.pendingDeliveries.update(item.id!, {
                        status: "FAILED",
                        error: result?.message || "Sync failed",
                    });
                }
            }
        } catch (error) {
            for (const item of pending) {
                await db.pendingDeliveries.update(item.id!, {
                    status: "FAILED",
                    error: "Sync request failed",
                });
            }
            throw error;
        }
    } catch (error) {
        console.error("Sync failed:", error);
    } finally {
        isSyncing = false;
        notifyListeners();
    }
};

export const getPendingCount = async (): Promise<number> => {
    return db.pendingDeliveries.where("status").anyOf(["PENDING", "SYNCING", "FAILED"]).count();
};

export const getLastSyncTime = async (): Promise<string | null> => {
    // Dexie's `where(...).equals(...)` returns a Collection, which has no `.orderBy()`
    // (that's a Table-only method) — sort the small synced set in memory instead.
    const synced = await db.pendingDeliveries.where("status").equals("SYNCED").toArray();
    if (synced.length === 0) {
        return null;
    }
    const latest = synced.reduce((latest, item) =>
        !latest.syncedAt || (item.syncedAt && item.syncedAt > latest.syncedAt) ? item : latest
    );
    return latest.syncedAt || null;
};
